// Shtrackx.jsx
// Page pública: consulta de Fecha Real de Zarpe (ATD) y Arribo (ATA) por Documento de Transporte.
// Formulario con react-hook-form. Captcha con script oficial de Cloudflare Turnstile (sin dependencia npm).
// Bundler: CRA -> variables de entorno con prefijo REACT_APP_.

import { useRef, useEffect, useCallback, useState } from "react";
import {
  Box,
  Input,
  Button,
  Label,
  TableLoader,
  StackRow,
  Stack,
  Heading,
} from "codekit";
import { useForm } from "react-hook-form";
import { useConsultarShipTrackMutation } from "../libs/redux/slices/Operacix/Operacix/shtrackxApiSlice";
import headerImg from "../Images/Head-Decreto-Map.jpg";

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const RECAPTCHA_SRC = "https://www.google.com/recaptcha/api.js?render=explicit";

export default function Shtrackx() {
  const captchaTokenRef = useRef("");   // token actual del captcha (no necesita re-render)
  const containerRef = useRef(null);    // div donde se monta el widget
  const widgetIdRef = useRef(null);     // id del widget para reset/remove

  const [consultar, { isLoading }] = useConsultarShipTrackMutation();

  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors },
  } = useForm({ mode: "onBlur" });

  const [msgResult, setMsgResult] = useState("");

  //Carga del script y render del widget
  useEffect(() => {
    let cancelado = false;
    const renderWidget = () => {
      if (cancelado || !window.grecaptcha || !containerRef.current) return;
      if (widgetIdRef.current !== null) return; // evita doble render

      // grecaptcha.render necesita que la API esté lista; ready() lo garantiza
      window.grecaptcha.ready(() => {
        widgetIdRef.current = window.grecaptcha.render(containerRef.current, {
          sitekey: RECAPTCHA_SITE_KEY,
          theme: "light",
          callback: (token) => { captchaTokenRef.current = token; },
          "expired-callback": () => { captchaTokenRef.current = ""; },
          "error-callback": () => { captchaTokenRef.current = ""; },
        });
      });
    };

    if (window.grecaptcha) {
      renderWidget();
    } else {
      let script = document.querySelector(`script[src="${RECAPTCHA_SRC}"]`);
      if (!script) {
        script = document.createElement("script");
        script.src = RECAPTCHA_SRC;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }
      script.addEventListener("load", renderWidget);
    }

    return () => {
      cancelado = true;
      // reCAPTCHA no expone un "remove"; reseteamos si hay widget montado
      if (window.grecaptcha && widgetIdRef.current !== null) {
        window.grecaptcha.reset(widgetIdRef.current);
      }
    };
  }, []);

  // Resetea el widget (el token es de un solo uso).
  const resetCaptcha = useCallback(() => {
    captchaTokenRef.current = "";
    if (window.grecaptcha && widgetIdRef.current !== null) {
      window.grecaptcha.reset(widgetIdRef.current);
    }
  }, []);

  // Limpia los campos de resultado.
  const limpiarResultado = () => {
    setValue("shfecatd", "");
    setValue("shfecata", "");
  };

  // --- Submit: RHF ya validó 'doctrans' (required) antes de llegar aquí ---
  const onSubmit = async (formData) => {
    const captchaToken = captchaTokenRef.current;

    // Validación: captcha SIEMPRE requerido antes de enviar.
    if (!captchaToken) {
      setMsgResult("Completa la verificación de seguridad (captcha).");
      return;
    }

    try {
      const res = await consultar({
        doctrans: formData.doctrans.trim(),
        captchat: captchaToken,
      }).unwrap();

      const attr = res?.data ?? {};

      // Llena los campos de resultado (solo lectura) vía RHF.
      setValue("shfecatd", attr.shfecatd || "");
      setValue("shfecata", attr.shfecata || "");

      if (res?.meta?.parcial) {
        // Existe pero una o ambas fechas nulas.
        setMsgResult(res.meta.mensaje);
      } else {
        setMsgResult("Consulta realizada correctamente.");
      }
    } catch (err) {
      limpiarResultado();
      const code = err?.data?.errors?.[0]?.code;
      const detail = err?.data?.errors?.[0]?.detail;

      if (code === "CAPTCHA_INVALIDO") {
        setMsgResult(detail);
      } else if (code === "DOCTRANS_NO_EXISTE") {
        setMsgResult(detail);
      } else {
        setMsgResult("Ocurrió un error al consultar. Intenta más tarde.");
      }
    } finally {
      // El token es de un solo uso: siempre se resetea tras enviar.
      resetCaptcha();
    }
  };

  return (
    <div
      className="flex items-center justify-center w-screen h-screen"
      style={{
        minHeight: "100vh",
        minWidth: "100vw",
        background: "#eef2f7",
        padding: "20px",
        boxSizing: "border-box",
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          spacing={4}
          border={1}
          bg="white"
          borderColor="neutral-300"
          style={{
            maxWidth: "720px",
            width: "100%",
            boxShadow: "0 8px 30px rgba(0,0,0,0.25)",
            borderRadius: "12px",
          }}
        >
          <img
            src={headerImg}
            alt="MapCargo"
            style={{
              width: "100%",
              display: "block",
              borderRadius: "12px 12px 0 0",
            }}
          />
          <Stack columns={1} gap={7}>
            <StackRow>

            </StackRow>
            <StackRow>
              <Label alignment="center">Número de Documento de Transporte (Guía Aérea o BL)*</Label>
              <Input
                {...register("doctrans", { required: true })}
                variant="white"
                type="text"
                placeholder="Diligéncialo tal cual aparece en la guía o BL"
                style={{ textAlign: "center" }}
              />
              {errors.doctrans && (
                <Label>Este campo es obligatorio.</Label>
              )}
            </StackRow>
            <StackRow>
              {/* Widget oficial de Cloudflare Turnstile */}
              <div ref={containerRef} style={{ display: "flex", justifyContent: "center" }}></div>
            </StackRow>
            <StackRow>
              <Button
                variant="success"
                block
                type="submit"
                style={{minWidth: "100%",}}
              >
              Consultar
              </Button>
            </StackRow>
            <StackRow>
              <Label alignment="center">Fecha Real de Despegue/Zarpe:</Label>
              <Input
                {...register("shfecatd")}
                variant="white"
                type="text"
                readOnly
                placeholder="-"
                style={{ textAlign: "center" }}
              />
            </StackRow>
            <StackRow>
              <Label alignment="center">Fecha Real de Arribo:</Label>
              <Input
                {...register("shfecata")}
                variant="white"
                type="text"
                readOnly
                placeholder="-"
                style={{ textAlign: "center" }}
              />
            </StackRow>
            <StackRow>
              <Label alignment="center">{msgResult}</Label>
            </StackRow>
          </Stack>
        </Box>
      </form>
    </div>
  );
}
